/**
 * See360 Interior Panorama Viewer
 * Three.js equirectangular panorama viewer for vehicle interiors.
 * Renders inside a given container — drag to look around, scroll to zoom.
 * Supports hotspot overlays projected from lon/lat sphere coordinates.
 */

import * as THREE from 'three';

export interface PanoramaHotspotData {
  id: string;
  lon: number;   // longitude on sphere (degrees) — stored as xPercent in DB
  lat: number;   // latitude on sphere (degrees) — stored as yPercent in DB
  label: string;
  description?: string;
  category?: string;
}

export interface PanoramaConfig {
  container: HTMLElement;
  imageUrl: string;
  hotspots?: PanoramaHotspotData[];
  autoRotate?: boolean;            // Auto-rotate the panorama (default: true)
  autoRotateSpeed?: number;        // Degrees per frame (default: 0.15)
  onReady?: () => void;
  onError?: (error: string) => void;
}

export class PanoramaViewer {
  private config: PanoramaConfig;
  private renderer: THREE.WebGLRenderer | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private scene: THREE.Scene | null = null;
  private animFrame = 0;
  private isDragging = false;
  private dragMoved = false;
  private prevMouse = { x: 0, y: 0 };
  private rotation = { lon: 180, lat: 0 };
  private fov = 100;
  private disposed = false;
  private resizeObserver: ResizeObserver | null = null;
  private autoRotateActive = true;
  private lastInteractionTime = 0;
  private autoRotateResumeDelay = 3000; // ms to wait before resuming auto-rotate

  // Hotspot overlay
  private hotspotOverlay: HTMLDivElement | null = null;
  private hotspotElements: Map<string, HTMLDivElement> = new Map();
  private activeTooltipId: string | null = null;

  constructor(config: PanoramaConfig) {
    this.config = config;
    this.init();
  }

  private init(): void {
    const { container } = this.config;

    // Scene
    this.scene = new THREE.Scene();

    // Camera
    this.camera = new THREE.PerspectiveCamera(
      100,
      container.clientWidth / container.clientHeight,
      0.1,
      1000
    );
    this.camera.position.set(0, 0, 0.1);

    // Renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x0a0e1a);
    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';
    this.renderer.domElement.style.cursor = 'grab';
    this.renderer.domElement.style.touchAction = 'none';
    container.appendChild(this.renderer.domElement);

    // Sphere geometry (inside-out)
    const geometry = new THREE.SphereGeometry(500, 64, 32);
    geometry.scale(-1, 1, 1);

    // Load image
    const img = new window.Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      if (this.disposed || !this.scene) return;
      const texture = new THREE.Texture(img);
      texture.needsUpdate = true;
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;

      const material = new THREE.MeshBasicMaterial({ map: texture });
      const mesh = new THREE.Mesh(geometry, material);
      this.scene.add(mesh);
      this.config.onReady?.();
    };

    img.onerror = () => {
      if (!this.disposed) {
        this.config.onError?.('Failed to load panorama image');
      }
    };

    img.src = this.config.imageUrl;

    // Create hotspot overlay layer
    this.createHotspotOverlay();

    // Animate
    this.animate();

    // Resize
    this.resizeObserver = new ResizeObserver(() => {
      if (!container || !this.camera || !this.renderer) return;
      this.camera.aspect = container.clientWidth / container.clientHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(container.clientWidth, container.clientHeight);
    });
    this.resizeObserver.observe(container);

    // Bind events
    this.bindEvents();
  }

  // ─── Hotspot Rendering ───────────────────────────────

  private createHotspotOverlay(): void {
    const hotspots = this.config.hotspots;
    if (!hotspots || hotspots.length === 0) return;

    // Inject pulse animation (once per page)
    if (!document.getElementById('see360-panorama-hotspot-styles')) {
      const style = document.createElement('style');
      style.id = 'see360-panorama-hotspot-styles';
      style.textContent = `
        @keyframes see360-pano-pulse {
          0%, 100% { box-shadow: 0 0 12px rgba(0, 212, 255, 0.4); }
          50% { box-shadow: 0 0 20px rgba(0, 212, 255, 0.7); }
        }
      `;
      document.head.appendChild(style);
    }

    // Create transparent overlay div that sits on top of the canvas
    this.hotspotOverlay = document.createElement('div');
    this.hotspotOverlay.style.cssText = `
      position: absolute; top: 0; left: 0; width: 100%; height: 100%;
      pointer-events: none; z-index: 10;
    `;
    this.config.container.appendChild(this.hotspotOverlay);

    // Create a wrapper for each hotspot
    for (const h of hotspots) {
      const wrapper = document.createElement('div');
      wrapper.style.cssText = `
        position: absolute; transform: translate(-50%, -50%);
        z-index: 30; pointer-events: auto; display: none;
      `;

      // Pulsing dot
      const dot = document.createElement('div');
      dot.style.cssText = `
        width: 24px; height: 24px; border-radius: 50%;
        background: rgba(0, 212, 255, 0.8); border: 2px solid white;
        cursor: pointer; transition: transform 0.2s, box-shadow 0.2s;
        box-shadow: 0 0 12px rgba(0, 212, 255, 0.4);
        animation: see360-pano-pulse 2s infinite;
      `;

      // Tooltip
      const tip = document.createElement('div');
      let html = `<strong style="font-size:13px;color:#fff">${h.label}</strong>`;
      if (h.category) html = `<span style="color:#00d4ff;font-size:10px;text-transform:uppercase;letter-spacing:1px">${h.category}</span><br>${html}`;
      if (h.description) html += `<br><span style="opacity:0.7;font-size:12px">${h.description}</span>`;
      tip.innerHTML = html;
      tip.style.cssText = `
        position: absolute; bottom: 32px; left: 50%; transform: translateX(-50%);
        background: rgba(0,0,0,0.85); color: white;
        padding: 10px 14px; border-radius: 8px; font-size: 14px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        max-width: 240px; min-width: 100px; white-space: normal;
        box-shadow: 0 4px 20px rgba(0,0,0,0.3); backdrop-filter: blur(8px);
        pointer-events: none; display: none; z-index: 100;
        border: 1px solid rgba(0,212,255,0.2);
      `;

      dot.addEventListener('mouseenter', () => {
        dot.style.transform = 'scale(1.3)';
        dot.style.boxShadow = '0 0 20px rgba(0, 212, 255, 0.8)';
        tip.style.display = 'block';
        this.activeTooltipId = h.id;
      });
      dot.addEventListener('mouseleave', () => {
        dot.style.transform = 'scale(1)';
        dot.style.boxShadow = '0 0 12px rgba(0, 212, 255, 0.4)';
        tip.style.display = 'none';
        this.activeTooltipId = null;
      });

      wrapper.appendChild(tip);
      wrapper.appendChild(dot);
      this.hotspotOverlay.appendChild(wrapper);
      this.hotspotElements.set(h.id, wrapper);
    }
  }

  /** Convert lon/lat to a 3D point on the sphere */
  private lonLatToVector3(lon: number, lat: number): THREE.Vector3 {
    const phi = THREE.MathUtils.degToRad(90 - lat);
    const theta = THREE.MathUtils.degToRad(lon);
    return new THREE.Vector3(
      500 * Math.sin(phi) * Math.cos(theta),
      500 * Math.cos(phi),
      500 * Math.sin(phi) * Math.sin(theta)
    );
  }

  /** Project a lon/lat to screen-space percentage */
  private projectToScreen(lon: number, lat: number): { x: number; y: number } | null {
    if (!this.camera || !this.renderer) return null;

    const point = this.lonLatToVector3(lon, lat);
    const projected = point.clone().project(this.camera);

    // Behind camera
    if (projected.z > 1) return null;

    const x = ((projected.x + 1) / 2) * 100;
    const y = ((1 - projected.y) / 2) * 100;

    // Off-screen (with margin)
    if (x < -10 || x > 110 || y < -10 || y > 110) return null;

    return { x, y };
  }

  /** Update hotspot positions based on current camera orientation */
  private updateHotspotPositions(): void {
    const hotspots = this.config.hotspots;
    if (!hotspots || hotspots.length === 0) return;

    for (const h of hotspots) {
      const el = this.hotspotElements.get(h.id);
      if (!el) continue;

      const pos = this.projectToScreen(h.lon, h.lat);
      if (pos) {
        el.style.display = 'block';
        el.style.left = `${pos.x}%`;
        el.style.top = `${pos.y}%`;
      } else {
        el.style.display = 'none';
      }
    }
  }

  // ─── Animation Loop ──────────────────────────────────

  private frameCounter = 0;

  private animate(): void {
    if (this.disposed) return;

    this.animFrame = requestAnimationFrame(() => this.animate());

    if (!this.camera || !this.renderer || !this.scene) return;

    const lat = Math.max(-85, Math.min(85, this.rotation.lat));
    this.rotation.lat = lat;

    const phi = THREE.MathUtils.degToRad(90 - lat);
    const theta = THREE.MathUtils.degToRad(this.rotation.lon);

    this.camera.lookAt(new THREE.Vector3(
      500 * Math.sin(phi) * Math.cos(theta),
      500 * Math.cos(phi),
      500 * Math.sin(phi) * Math.sin(theta)
    ));

    this.camera.fov = this.fov;
    this.camera.updateProjectionMatrix();

    // Auto-rotate when not dragging (slow pan)
    if (this.autoRotateActive && !this.isDragging) {
      const now = Date.now();
      if (now - this.lastInteractionTime > this.autoRotateResumeDelay) {
        const speed = this.config.autoRotateSpeed || 0.15;
        this.rotation.lon += speed;
      }
    }

    this.renderer.render(this.scene, this.camera);

    // Update hotspot positions every 3 frames (~20 updates/sec at 60fps)
    this.frameCounter++;
    if (this.frameCounter % 3 === 0) {
      this.updateHotspotPositions();
    }
  }

  private bindEvents(): void {
    const el = this.renderer!.domElement;

    // Mouse
    el.addEventListener('mousedown', (e) => {
      this.isDragging = true;
      this.dragMoved = false;
      this.prevMouse = { x: e.clientX, y: e.clientY };
      this.lastInteractionTime = Date.now();
      el.style.cursor = 'grabbing';
    });

    window.addEventListener('mousemove', (e) => {
      if (!this.isDragging) return;
      const dx = e.clientX - this.prevMouse.x;
      const dy = e.clientY - this.prevMouse.y;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) this.dragMoved = true;
      const s = this.fov / 75 * 0.25;
      this.rotation.lon -= dx * s;
      this.rotation.lat += dy * s;
      this.prevMouse = { x: e.clientX, y: e.clientY };
    });

    window.addEventListener('mouseup', () => {
      this.isDragging = false;
      this.lastInteractionTime = Date.now();
      if (this.renderer) this.renderer.domElement.style.cursor = 'grab';
    });

    // Touch
    el.addEventListener('touchstart', (e) => {
      if (e.touches.length === 1) {
        e.preventDefault();
        this.isDragging = true;
        this.dragMoved = false;
        this.lastInteractionTime = Date.now();
        this.prevMouse = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    }, { passive: false });

    el.addEventListener('touchmove', (e) => {
      if (!this.isDragging || e.touches.length !== 1) return;
      e.preventDefault();
      const dx = e.touches[0].clientX - this.prevMouse.x;
      const dy = e.touches[0].clientY - this.prevMouse.y;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) this.dragMoved = true;
      const s = this.fov / 75 * 0.25;
      this.rotation.lon -= dx * s;
      this.rotation.lat += dy * s;
      this.prevMouse = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }, { passive: false });

    el.addEventListener('touchend', () => { 
      this.isDragging = false; 
      this.lastInteractionTime = Date.now();
    });

    // Scroll zoom
    el.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.fov = Math.max(30, Math.min(100, this.fov + e.deltaY * 0.05));
      this.lastInteractionTime = Date.now();
    }, { passive: false });
  }

  // ─── Public auto-rotate control ─────────────────────

  toggleAutoRotate(): void {
    this.autoRotateActive = !this.autoRotateActive;
    if (this.autoRotateActive) {
      this.lastInteractionTime = 0; // Resume immediately
    }
  }

  startAutoRotate(): void {
    this.autoRotateActive = true;
    this.lastInteractionTime = 0;
  }

  stopAutoRotate(): void {
    this.autoRotateActive = false;
  }

  isAutoRotating(): boolean {
    return this.autoRotateActive;
  }

  destroy(): void {
    this.disposed = true;
    cancelAnimationFrame(this.animFrame);
    this.resizeObserver?.disconnect();
    this.renderer?.dispose();
    if (this.renderer && this.config.container.contains(this.renderer.domElement)) {
      this.config.container.removeChild(this.renderer.domElement);
    }
    // Remove hotspot overlay
    this.hotspotOverlay?.remove();
    this.hotspotElements.clear();
    this.renderer = null;
    this.camera = null;
    this.scene = null;
  }
}
